import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { type Theme, useMediaQuery, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import ButtonDanger from '../ButtonDanger';
import ConfirmButton from '../ConfirmButton';
import messages from './messages';

interface IServicePanelProps {
  active?: boolean;
  children: ReactNode;
  disabled?: boolean;
  hasDefaultPassword?: boolean;
  icon?: ReactNode;
  isServiceAvailable?: boolean;
  isToggleConfirmDisabled?: boolean;
  name?: ReactNode;
  onToggleConfirm?: () => void;
  state?: ReactNode;
  testId?: string;
  toggleConfirmInfo: ReactNode;
  toggleConfirmTitle?: string | ReactElement;
}

const styles = {
  accordion: {
    '& .Mui-disabled img,.Mui-disabled svg': {
      filter: 'grayscale(100%)',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      my: 1.5,
    },
  },
  img: {
    '& img, svg': {
      height: '40px',
      width: '40px',
    },
    width: (theme: Theme) => theme.spacing(7),
  },
  inactiveImg: {
    '& img': {
      filter: 'grayscale(100%)',
      opacity: (theme: Theme) => theme.palette.action.disabledOpacity,
    },
  },
  notSet: {
    cursor: 'default',
  },
};

// eslint-disable-next-line complexity
const ServicePanel = ({
  active = false,
  children,
  disabled = false,
  hasDefaultPassword,
  icon = null,
  isServiceAvailable = true,
  isToggleConfirmDisabled = false,
  name = null,
  onToggleConfirm = () => {},
  state = null,
  testId,
  toggleConfirmInfo,
  toggleConfirmTitle,
}: IServicePanelProps) => {
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isServiceNotSet = disabled && isServiceAvailable;

  const getSummaryInfoSettings = () =>
    upLg
      ? {
          alignItems: 'center' as const,
          direction: 'row' as const,
          spacing: 2,
        }
      : {
          alignItems: 'flex-start' as const,
          direction: 'column' as const,
        };

  const getButtonContainerSettings = () =>
    downSm
      ? {
          justifyContent: 'flex-start',
        }
      : {
          columnSpacing: 1,
          justifyContent: 'flex-end',
        };

  const getStateColor = () => {
    if (hasDefaultPassword) {
      return 'error.main';
    }
    if (active && !disabled) {
      return 'success.main';
    }
    if (!active && !disabled) {
      return 'warning.main';
    }
    return 'text.primary';
  };

  return (
    <Box mb={3}>
      <Accordion sx={styles.accordion}>
        <AccordionSummary
          data-testid={testId}
          disabled={disabled && !isServiceAvailable}
          expandIcon={!isServiceNotSet && <ExpandMoreIcon />}
          sx={{ ...(isServiceNotSet && styles.notSet) }}
        >
          <Grid
            alignItems="flex-start"
            container
            sx={{
              ...styles.img,
              ...(((!active && !disabled) || isServiceNotSet) && styles.inactiveImg),
            }}
          >
            {icon && (
              <Grid>
                <Box pr={2} width={40}>
                  {icon}
                </Box>
              </Grid>
            )}
          </Grid>
          <Grid alignItems="center" container size="grow">
            <Grid {...getSummaryInfoSettings()} container justifyContent="flex-start" size={{ md: 8, xs: 12 }}>
              {name && (
                <Grid>
                  <Typography component="div">{name}</Typography>
                </Grid>
              )}
              {state && !disabled && (
                <Grid mt={0.4}>
                  <Typography component="div">
                    <Box color={getStateColor()}>{state}</Box>
                  </Typography>
                </Grid>
              )}
              {isServiceNotSet && (
                <Grid mt={0.4}>
                  <Typography component="div">
                    <strong>
                      <FormattedMessage {...messages.servicePanelStateDisabled} />
                    </strong>
                  </Typography>
                </Grid>
              )}
              {disabled && !isServiceNotSet && (
                <Grid mt={0.4}>
                  <Typography component="div">
                    <strong>
                      <FormattedMessage {...messages.servicePanelStateUnavailable} />
                    </strong>
                  </Typography>
                </Grid>
              )}
            </Grid>
            {onToggleConfirm && isServiceAvailable && (
              <Grid {...getButtonContainerSettings()} container size={{ md: 4, xs: 12 }}>
                <Grid>
                  <ConfirmButton
                    confirmInfo={toggleConfirmInfo}
                    dialogHeaderVariant={active ? 'danger' : 'primary'}
                    dialogTitle={toggleConfirmTitle}
                    disableButtonText={
                      <div>
                        <Box sx={{ display: { lg: 'block ', xs: 'none' } }}>
                          <FormattedMessage {...messages.servicePanelButtonDisableShort} />
                        </Box>
                        <Box sx={{ display: { lg: 'none ', xs: 'block' } }}>
                          <FormattedMessage {...messages.servicePanelButtonDisable} />
                        </Box>
                      </div>
                    }
                    enableButtonText={
                      <div>
                        <Box sx={{ display: { lg: 'block ', xs: 'none' } }}>
                          <FormattedMessage {...messages.servicePanelButtonEnableShort} />
                        </Box>
                        <Box sx={{ display: { lg: 'none ', xs: 'block' } }}>
                          <FormattedMessage {...messages.servicePanelButtonEnable} />
                        </Box>
                      </div>
                    }
                    isActive={active}
                    onConfirm={onToggleConfirm}
                    submitButton={(onConfirm) => (
                      <>
                        {active ? (
                          <ButtonDanger onClick={onConfirm} startIcon={<HighlightOffIcon />}>
                            <FormattedMessage {...messages.servicePanelDialogButtonTextDeactivate} />
                          </ButtonDanger>
                        ) : (
                          <Button
                            color={'primary'}
                            onClick={onConfirm}
                            startIcon={<DoneIcon />}
                            variant="contained"
                            disabled={isToggleConfirmDisabled}
                          >
                            <FormattedMessage {...messages.servicePanelDialogButtonTextActivate} />
                          </Button>
                        )}
                      </>
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </AccordionSummary>
        {!disabled && (
          <AccordionDetails>
            <Grid container direction="column">
              <Grid>
                <Divider />
              </Grid>
              <Grid>
                <Box py={2}>{children}</Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  );
};

export default ServicePanel;
